/* eslint-disable dot-notation */
/* eslint-disable no-cond-assign */
/* eslint-disable prefer-object-spread */

import { matchPath } from 'react-router';

import dayjs from 'dayjs';

/* Localisation strings */
import { MDText } from 'i18n-react';

import {
  excludeMileagePath,
  getBlogTitle,
  getURLPath,
  isBlogURL,
  isGuidesURL,
  replaceHyphensWithSpace,
} from 'Utilities/breadcrumbs';
import { toTitleCase } from 'Utilities/formatter';
import { NO_PAGE_TITLE_SUFFIX_PATHS } from 'Utilities/helpers';
import { FULL_LANDING_PAGE_ROUTES, SELL_MY_CAR_PATH } from 'Utilities/wordpress';

import Schema from '../schema.json';
import Texts from '../texts.json';

const T = new MDText(Texts);

let schema = JSON.stringify(Schema);

const isLandingPageRoute = (url) => FULL_LANDING_PAGE_ROUTES.includes(url);
const isBrandsRoute = (url) => (/\/brands\/.*/.test(url));
const isSellMyCarUrl = (url) => SELL_MY_CAR_PATH === url;
const isLandingPageBelowTheFoldExperiment = (featureFlags) => featureFlags?.['showNewLandingPageBelowTheFoldDesign'];

export default {
  client: (typeof document === 'object'),
  descriptionTag: null,
  formatDescription(description) {
    if (this.client && description) {
      if (!this.descriptionTag) {
        this.descriptionTag = document.querySelector('meta[name="description"]');
      }

      this.descriptionTag.setAttribute('content', description);
    }

    return description;
  },
  formatSchema(reviewsData, description, part, url, wordpressData, youtubeEmbedURL, featureFlags) {
    /**
     * Try/Catch is temp since it's site wide, will remove later on
     */
    try {
      /**
       * TODO;
       * This needs to be made more consistent,
       * it's there is any work done of the WP API,
       * we should make pages/posts consistent in structure and data provided
       */
      const isBlog = this.isBlog(url);
      const isGuide = this.isGuide(url);

      const schemaPart = JSON.parse(schema);

      const createBreadcrumbData = (arr) => {
        let prevPath = 'https://motorway.co.uk';

        const crumbs = arr.map((it, index) => {
          prevPath += `/${it}`;
          return {
            '@type': 'ListItem',
            item: prevPath,
            name: isBlogURL(url) ? getBlogTitle(this.isBlog(getURLPath(prevPath)))
              : toTitleCase(replaceHyphensWithSpace(it)),
            position: index + 2,
          };
        });

        // We don't need to show the current page
        crumbs.pop();

        return schemaPart.BreadcrumbList.itemListElement.concat(crumbs);
      };

      const createFAQSchemaData = () => {
        const acf = wordpressData.content;
        const questionKey = 'faq_question';

        return Object.entries(acf).reduce((acc, [name, val]) => {
          if (name.includes(questionKey) && val) {
            const keys = name.split(questionKey);
            const answer = acf[`faq_answer${keys[1]}`];
            if (answer) {
              acc.push({
                '@type': 'Question',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: answer,
                },
                name: val,
              });
            }
          }
          return acc;
        }, []);
      };

      if (part === 'Article') {
        part = 'ListItem';

        if (isBlog.params.post || isGuidesURL(url)) {
          part = 'NewsArticle';
        }
      }

      if (reviewsData && reviewsData.rating && description) {
        schema = schema.replace(/{{reviewRating}}/g, reviewsData.rating * 5);
        schema = schema.replace(/{{reviewCount}}/g, reviewsData.totalReviews);
        schema = schema.replace(/{{description}}/g, description);
      } else {
        return '';
      }

      if (part === 'FAQPage') {
        if (isLandingPageRoute(url)) {
          const faqSchemaData = createFAQSchemaData();

          schemaPart.FAQPage.mainEntity = [];

          if (Array.isArray(faqSchemaData) && faqSchemaData.length) {
            schemaPart.FAQPage.mainEntity = faqSchemaData;
            return `<script type="application/ld+json">${JSON.stringify(schemaPart.FAQPage)}</script>`;
          }
          return '';
        }
        return '';
      }

      if (part === 'BreadcrumbList') {
        const parts = excludeMileagePath(url);

        // More than one level deep
        if (parts.length > 1) {
          const breadcrumbSchemaData = createBreadcrumbData(parts);
          schemaPart.BreadcrumbList.itemListElement = breadcrumbSchemaData;

          if (Array.isArray(breadcrumbSchemaData) && breadcrumbSchemaData.length) {
            return `<script type="application/ld+json">${JSON.stringify(schemaPart.BreadcrumbList)}</script>`;
          }
          return '';
        }
        return '';
      }

      if (part === 'VideoObject') {
        if (isSellMyCarUrl(url) && isLandingPageBelowTheFoldExperiment(featureFlags)) {
          return '';
        }
        if (url === '/' || isBrandsRoute(url) || isLandingPageRoute(url)) {
          let videoSchema = JSON.stringify(schemaPart[part]);
          videoSchema = videoSchema.replace(/{{youtubeEmbedURL}}/g, youtubeEmbedURL);
          return `<script type="application/ld+json">${videoSchema}</script>`;
        }
        return '';
      }

      if (part === 'HowToSellVideoObject') {
        if (isSellMyCarUrl(url) && isLandingPageBelowTheFoldExperiment(featureFlags)) {
          return `<script type="application/ld+json">${JSON.stringify(schemaPart[part])}</script>`;
        }
        return '';
      }

      if (!schemaPart[part]) {
        return '';
      }
      if (!wordpressData) {
        return (part !== 'ListItem') ? `<script type="application/ld+json">${JSON.stringify(schemaPart[part])}</script>` : '';
      }

      if ((isGuide.isSection) && part === 'ListItem') {
        const section = 'guides';
        let contentList = [];
        contentList = [wordpressData.content];

        if (contentList && contentList[0]) {
          schemaPart.ListItem.itemListElement = [];

          const linkRegex = /<a href="\/guides(.*)">(.*)<\/a>/g;
          let match;
          let i = 1;
          while ((match = linkRegex.exec(contentList[0])) !== null) {
            if (!match[1] || !match[2]) {
              return '';
            }

            const newItemList = {
              '@type': 'ListItem',
              // eslint-disable-next-line quote-props
              'position': i++,
            };

            newItemList.url = `http://motorway.co.uk/${section}${match[1]}`;

            schemaPart.ListItem.itemListElement.push(newItemList);
          }

          return `<script type="application/ld+json">${JSON.stringify(schemaPart.ListItem)}</script>`;
        }
        return '';
      }

      if (part === 'ListItem') {
        if (!wordpressData.posts) {
          return '';
        }
        if (wordpressData.posts && wordpressData.posts.length === 0) {
          return '';
        }

        const itemList = Object.assign({}, schemaPart['ListItem'].itemListElement[0]);
        schemaPart.ListItem.itemListElement = [];

        wordpressData.posts.forEach((post, i) => {
          let newItemList = JSON.stringify(itemList);

          newItemList = newItemList.replace(/{{position}}/g, i + 1);
          newItemList = newItemList.replace(/{{articleURL}}/g, post.slug);

          schemaPart.ListItem.itemListElement.push(JSON.parse(newItemList));
        });
      } else if (part === 'NewsArticle') {
        let newNewsArticle = JSON.stringify(schemaPart.NewsArticle);

        const post = wordpressData.posts[0];

        if (post) {
          let date = post.date.match(/([0-9]{1,2})(st|nd|rd|th)/);
          const hasAuthorName = post.author && post.author.name;

          date = post.date.replace(date[0], date[1]);
          newNewsArticle = newNewsArticle.replace(/{{articleDate}}/g, dayjs(new Date(date)).format('YYYY-MM-DD'));
          newNewsArticle = newNewsArticle.replace(/{{articleAuthor}}/g, hasAuthorName ? post.author.name : `${T.translate('appName')}`);
          newNewsArticle = newNewsArticle.replace(/{{articleAuthorType}}/g, (hasAuthorName && (post.author.name === T.translate('appName'))) ? `${T.translate('authorType.organization')}` : `${T.translate('authorType.person')}`);
          newNewsArticle = newNewsArticle.replace(/{{articleHeadline}}/g, post.title);
        } else if (wordpressData.meta && wordpressData.meta.published && wordpressData.meta.title) {
          newNewsArticle = newNewsArticle.replace(/{{articleDate}}/g, wordpressData.meta.published);
          newNewsArticle = newNewsArticle.replace(/{{articleAuthor}}/g, `${T.translate('appName')}`);
          newNewsArticle = newNewsArticle.replace(/{{articleAuthorType}}/g, `${T.translate('authorType.organization')}`);
          newNewsArticle = newNewsArticle.replace(/{{articleHeadline}}/g, wordpressData.meta.title);
        } else {
          return '';
        }

        newNewsArticle = newNewsArticle.replace(/{{articleURL}}/g, url);
        schemaPart.NewsArticle = JSON.parse(newNewsArticle);
      }

      return `<script type="application/ld+json">${JSON.stringify(schemaPart[part])}</script>`;
    } catch (e) {
      return `
        <script>
          if (window.Sentry) {
            Sentry.withScope(function(scope) {
              scope.setLevel('error');
              scope.setTag('schema', 'error');
              scope.setExtra('error', \`${e}\`);
              Sentry.captureMessage(\`Schema Error: ${part}\`);
            });
          }
        </script>
      `;
    }
  },
  formatTitle(path, title) {
    if (!path || !title) {
      return title;
    }

    if (title.length === 0) {
      title = `${T.translate('appName')}`;
    } else if (NO_PAGE_TITLE_SUFFIX_PATHS.includes(path)) {
      title = `${title}`;
    } else if (/^\/blog\/[0-9]{4}/.test(path) && !this.isBlog(path).params.post) {
      const dates = path.split('/').splice(2, 4);
      const format = ['Do', 'MMMM', 'YYYY'];

      // eslint-disable-next-line no-restricted-globals
      let dayjsDate = format.map((_, i) => ((!isNaN(dates[i])) ? dates[i].padStart(2, '0') : '01'));

      dayjsDate = dayjs(dayjsDate.join('-')).format(format.join(' '));
      dayjsDate = dayjsDate.split(' ').reverse().filter((_, i) => dates[i]).reverse()
        .join(' ');

      title = `${T.translate('pageTitle.blog.title', {
        date: dayjsDate,
      })}`;
    } else {
      title = `${title}${T.translate('pageTitle.suffix')}`;
    }

    if (this.client) {
      document.title = title;
    }

    return title;
  },
  isBlog(path) {
    const isBlog = matchPath(path, { path: '/:blog?/:year?/:month?/:day?/:post?' });
    isBlog.isSection = (isBlog.params.blog === 'blog');
    isBlog.params = (isBlog.isSection) ? isBlog.params : {};
    return isBlog;
  },
  isGuide(path) {
    const isGuide = matchPath(path, { path: '/:guides?/:guide?' });
    isGuide.isSection = (isGuide.params.guides === 'guides');
    isGuide.params = (isGuide.isSection) ? isGuide.params : {};
    return isGuide;
  },
};
